import React from 'react';
import { Box, Typography, Switch, FormControlLabel, IconButton, Tooltip, useMediaQuery, useTheme, SxProps, Theme } from '@mui/material';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import HourlyForecastTimeline from './HourlyForecastTimeline';
import { HourlyForecastTimelineData } from '../../models/HourlyForecastTimelineData';
import StatusAlert from '../common/StatusAlert';
import { VisibilityLegend } from '../common/VisibilityLegend';

interface ForecastTimelineContainerProps {
  // Array of timeline data for multiple locations
  timelineData: HourlyForecastTimelineData[];
  // Title for the container
  title?: string;
  customNow?: Date | null; // Add custom now prop
  sx?: SxProps<Theme>; // Add sx prop support
  useUTCTime?: boolean; // UTC time toggle state
  onTimezoneToggle?: () => void; // Timezone toggle handler
  onAdjustDate?: () => void; // Date adjustment handler
  hasDataError?: boolean;
  dataQualityWarning?: { type: 'error' | 'warning'; title: string; message: string } | null;
  fogStatus?: any;
  isLoading?: boolean;
  timeNavigationControls?: React.ReactNode; // Optional time navigation controls to render above header
}

const ForecastTimelineContainer: React.FC<ForecastTimelineContainerProps> = ({
  timelineData,
  title = "Current Forecasts",
  customNow = null, // Default to null
  sx,
  useUTCTime = true, // Default to UTC time
  onTimezoneToggle,
  onAdjustDate,
  hasDataError,
  dataQualityWarning,
  fogStatus,
  isLoading,
  timeNavigationControls
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine if we're using a custom date from the URL
  const hasCustomDate = !!customNow;

  // Check if any timeline has operating hours restrictions
  const hasOutOfHoursLocations = timelineData.some(data => data.operatingHours !== undefined);

  return (
    <Box sx={{ width: '100%', mb: { xs: 3, md: 4 }, ...sx }}>
      {/* Sticky container for header, status alert, and legend */}
      <Box sx={{
        position: 'relative',
        top: 'auto',
        zIndex: 'auto',
        backgroundColor: 'transparent',
        mx: 0,
        px: 0,
        pt: 0,
        pb: 0,
        // Only apply sticky on tablets+ in portrait/landscape (excludes mobile landscape)
        '@media (min-width: 600px) and (min-height: 500px)': {
          position: 'sticky',
          top: '-24px', // Pulls sticky container up to cover gap from Container padding
          zIndex: 3,
          backgroundColor: 'background.default',
          mx: -3,
          px: 3,
          pt: 3,
          pb: 0.5
        }
      }}>
        {/* Time Navigation Controls (if provided) */}
        {timeNavigationControls && (
          <Box sx={{
            mt: { xs: 0, sm: 1 },  // Add margin top on tablets+
            mb: 2
          }}>
            {timeNavigationControls}
          </Box>
        )}

        {/* Header with title and timezone toggle */}
        <Box sx={{
          mb: { xs: 1.5, md: 2 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: { xs: 1.5, sm: 2 },
          gap: { xs: 1, sm: 0 },
          pt: { xs: 0, sm: 1 }
        }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
            }}
          >
            {title}
            {hasCustomDate && (
              <Typography
                component="span"
                variant="subtitle1"
                sx={{
                  ml: { xs: 1, sm: 2 },
                  color: 'warning.main',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  display: { xs: 'block', sm: 'inline' },
                  mt: { xs: 0.5, sm: 0 }
                }}
              >
                (Using custom date)
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onAdjustDate && (
              <Tooltip title="Adjust Date" placement="left">
                <IconButton
                  onClick={onAdjustDate}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <EditCalendarIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={useUTCTime}
                  onChange={onTimezoneToggle}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    ml: 0.5,
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}
                >
                  {useUTCTime ? "UTC Time" : "Local Time"}
                </Typography>
              }
              sx={{
                my: { xs: 0.5, sm: 0 },
                mr: { xs: 0, sm: 0 },
                '.MuiFormControlLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '1rem' }
                }
              }}
            />
          </Box>
        </Box>

        {/* Status alert - shown below header divider */}
        {(fogStatus || isLoading) && (
          <Box sx={{ mb: 2 }}>
            <StatusAlert
              fogStatus={fogStatus}
              hasDataError={hasDataError}
              dataQualityWarning={dataQualityWarning}
              isLoading={isLoading}
            />
          </Box>
        )}

        {/* Visibility Legend */}
        <VisibilityLegend orientation="horizontal" showOutOfHours={hasOutOfHoursLocations} />
      </Box>

      {/* Render all timeline graphs with the shared timezone setting */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {timelineData.map((data, index) => (
          <HourlyForecastTimeline
            key={`timeline-${data.location_name}-${index}`}
            timelineData={data}
            useUTCTime={useUTCTime}
            customNow={customNow}
            hideLegend={true}
            hideZoomIcon={false}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ForecastTimelineContainer; 